import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";

import { Button, Card, Checkbox, FormField, Skeleton, StatusBadge } from "../ui";
import CampanhaMetaBar, { ICONE_OPCOES } from "../CampanhaMetaBar";
import { listarBancosAtivos, listarConveniosAtivos } from "../../services/joinService";
import { listarCampanhas, removerCampanha, salvarCampanha } from "../../services/campanhaService";
import { SwalWithTheme } from "../../utils/sweetalert";

const METRICAS = [
  { value: "PRODUCAO_LIQUIDA", label: "Produção líquida" },
  { value: "PRODUCAO_BRUTA", label: "Produção bruta" },
  { value: "PRODUCAO_COMISSIONADA", label: "Produção comissionada" },
  { value: "CONTRATOS", label: "Contratos" },
];

const hoje = () => new Date().toISOString().slice(0, 10);

const CAMPANHA_VAZIA = {
  id: null,
  tipo: "BANCO",
  referenciaId: "",
  rotulo: "",
  metrica: "PRODUCAO_LIQUIDA",
  meta: "",
  dataInicio: hoje(),
  dataFim: hoje(),
  icone: "flame",
  cor: "#d9a300",
  ativo: true,
};

/**
 * Campanhas de destaque nos rankings de banco e convênio.
 *
 * A métrica é escolhida por campanha porque o ranking já mostra as quatro (bruta, líquida,
 * comissionada e contratos): fixar uma faria a barra divergir do número exibido ao lado dela.
 */
export default function CampanhasSection() {
  const [campanhas, setCampanhas] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [convenios, setConvenios] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [edicao, setEdicao] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [lista, bancosJoin, conveniosJoin] = await Promise.all([
        listarCampanhas().catch(() => []),
        listarBancosAtivos().catch(() => []),
        listarConveniosAtivos().catch(() => []),
      ]);
      setCampanhas(lista);
      setBancos(bancosJoin);
      setConvenios(conveniosJoin);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const opcoesReferencia = useMemo(() => {
    const origem = edicao?.tipo === "CONVENIO" ? convenios : bancos;
    return origem.map((item) => ({
      value: String(item.convenioId ?? item.bancoId ?? item.id),
      label: item.convenioNome ?? item.bancoNome ?? item.nome,
    }));
  }, [edicao?.tipo, bancos, convenios]);

  const nomeDaReferencia = useCallback((campanha) => {
    const origem = campanha.tipo === "CONVENIO" ? convenios : bancos;
    const achado = origem.find(
      (item) => String(item.convenioId ?? item.bancoId ?? item.id) === String(campanha.referenciaId)
    );
    return achado?.convenioNome ?? achado?.bancoNome ?? achado?.nome ?? `#${campanha.referenciaId}`;
  }, [bancos, convenios]);

  const salvar = async () => {
    setSalvando(true);
    try {
      await salvarCampanha({ ...edicao, meta: Number(edicao.meta) });
      setEdicao(null);
      await carregar();
      SwalWithTheme.success("Pronto", "Campanha salva.");
    } catch (erro) {
      SwalWithTheme.error("Não deu", erro?.response?.data?.message || "Não foi possível salvar a campanha.");
    } finally {
      setSalvando(false);
    }
  };

  const remover = async (campanha) => {
    const confirmacao = await SwalWithTheme.fire({
      title: `Remover "${campanha.rotulo}"?`,
      text: "A linha do ranking volta ao normal, sem meta.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remover",
      cancelButtonText: "Cancelar",
    });
    if (!confirmacao.isConfirmed) return;
    try {
      await removerCampanha(campanha.id);
      await carregar();
    } catch (erro) {
      SwalWithTheme.error("Não deu", erro?.response?.data?.message || "Não foi possível remover.");
    }
  };

  return (
    <Card
      title="Campanhas"
      actions={[
        <Button key="nova-campanha" size="sm" onClick={() => setEdicao({ ...CAMPANHA_VAZIA })} disabled={carregando}>
          <Plus className="h-4 w-4" />
          Nova campanha
        </Button>,
      ]}
    >
      <p className="mb-4 text-sm text-muted-foreground">
        A linha do banco ou convênio em campanha ganha ícone, meta e barra de progresso no ranking do Dashboard e da
        Vida do agente. O progresso é medido <strong className="text-foreground">no período da campanha</strong> e na
        carteira inteira — não muda conforme o filtro de quem está olhando. No máximo três campanhas ativas ao mesmo
        tempo, porque cada uma custa uma consulta a mais na Join.
      </p>

      {carregando ? (
        <Skeleton className="h-20 w-full rounded-xl" />
      ) : (
        <div className="space-y-3">
          {campanhas.length === 0 ? (
            <p className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
              Nenhuma campanha cadastrada.
            </p>
          ) : null}
          {campanhas.map((campanha) => (
            <div key={campanha.id} className="rounded-xl border border-border p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-foreground">{campanha.rotulo}</span>
                    <StatusBadge status={campanha.tipo === "BANCO" ? "Banco" : "Convênio"} variant="info" />
                    {!campanha.ativo ? <StatusBadge status="Inativa" variant="danger" /> : null}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {nomeDaReferencia(campanha)} ·{" "}
                    {METRICAS.find((metrica) => metrica.value === campanha.metrica)?.label} ·{" "}
                    {campanha.dataInicio} a {campanha.dataFim}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEdicao({ ...campanha, meta: String(campanha.meta) })}>
                    Editar
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remover(campanha)} aria-label={`Remover ${campanha.rotulo}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {/* Prévia igual à do ranking, para o admin ver o que o time vai ver. */}
              <CampanhaMetaBar campanha={campanha} />
            </div>
          ))}
        </div>
      )}

      {edicao ? (
        <div className="mt-4 space-y-4 rounded-2xl border border-border bg-muted/20 p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              label="Nome da campanha"
              value={edicao.rotulo}
              placeholder="Outubro C6"
              onChange={(event) => setEdicao((atual) => ({ ...atual, rotulo: event.target.value }))}
            />
            <FormField
              label="Tipo"
              type="select"
              value={edicao.tipo}
              onChange={(event) => setEdicao((atual) => ({ ...atual, tipo: event.target.value, referenciaId: "" }))}
              options={[{ value: "BANCO", label: "Banco" }, { value: "CONVENIO", label: "Convênio" }]}
            />
            <FormField
              label={edicao.tipo === "CONVENIO" ? "Convênio" : "Banco"}
              type="select"
              value={edicao.referenciaId}
              onChange={(event) => setEdicao((atual) => ({ ...atual, referenciaId: event.target.value }))}
              options={[{ value: "", label: "Selecione..." }, ...opcoesReferencia]}
            />
            <FormField
              label="Métrica da meta"
              type="select"
              value={edicao.metrica}
              onChange={(event) => setEdicao((atual) => ({ ...atual, metrica: event.target.value }))}
              options={METRICAS}
            />
            <FormField
              label={edicao.metrica === "CONTRATOS" ? "Meta (contratos)" : "Meta (R$)"}
              type="number"
              min="0"
              value={edicao.meta}
              onChange={(event) => setEdicao((atual) => ({ ...atual, meta: event.target.value }))}
            />
            {/* Cor da marca do banco: e dado da campanha, nao decisao de tema -- por isso hex livre. */}
            <div className="space-y-1.5">
              <span className="block text-sm font-medium text-foreground">Cor do destaque</span>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={edicao.cor || "#d9a300"}
                  onChange={(event) => setEdicao((atual) => ({ ...atual, cor: event.target.value }))}
                  className="h-10 w-14 cursor-pointer rounded-md border border-border bg-background p-1"
                  aria-label="Cor do destaque"
                />
                <input
                  type="text"
                  value={edicao.cor || ""}
                  placeholder="#d9a300"
                  onChange={(event) => setEdicao((atual) => ({ ...atual, cor: event.target.value }))}
                  className="h-10 w-28 rounded-md border border-border bg-background px-3 text-sm text-foreground"
                  aria-label="Cor em hexadecimal"
                />
              </div>
              <p className="text-xs text-muted-foreground">Use a cor da marca do banco. Em branco, usa o dourado.</p>
            </div>

            <FormField
              label="Ícone"
              type="select"
              value={edicao.icone}
              onChange={(event) => setEdicao((atual) => ({ ...atual, icone: event.target.value }))}
              options={ICONE_OPCOES}
            />
            <FormField
              label="Início"
              type="date"
              value={edicao.dataInicio}
              onChange={(event) => setEdicao((atual) => ({ ...atual, dataInicio: event.target.value }))}
            />
            <FormField
              label="Fim"
              type="date"
              value={edicao.dataFim}
              onChange={(event) => setEdicao((atual) => ({ ...atual, dataFim: event.target.value }))}
            />
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-foreground">
            <Checkbox
              checked={edicao.ativo}
              onCheckedChange={(valor) => setEdicao((atual) => ({ ...atual, ativo: valor }))}
            />
            Ativa
          </label>

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setEdicao(null)}>Cancelar</Button>
            <Button size="sm" onClick={salvar} disabled={salvando}>
              {salvando ? "Salvando..." : "Salvar campanha"}
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
